import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Radio, ShieldCheck, MonitorSmartphone, MapPin, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";

const RED = "#c00000";

export default function Login() {
  const { user, login } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showInterest, setShowInterest] = useState(false);
  const [iName, setIName] = useState("");
  const [iEmail, setIEmail] = useState("");
  const [iCompany, setICompany] = useState("");
  const [iMsg, setIMsg] = useState("");
  const [iPass, setIPass] = useState("");
  const [iLoading, setILoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(lang === "de" ? "Angemeldet" : "Signed in");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const de = lang === "de";

  const submitInterest = async (e) => {
    e.preventDefault();
    if (iPass.length < 6) { toast.error(de ? "Passwort mindestens 6 Zeichen" : "Password min. 6 chars"); return; }
    setILoading(true);
    try {
      await api.post("/cportal/register", { company: iCompany, name: iName, email: iEmail, password: iPass, message: iMsg });
      toast.success(de ? "Registrierung eingegangen – Ihr Zugang wird nach Prüfung freigeschaltet." : "Registration received – access will be activated after review.");
      setIName(""); setIEmail(""); setICompany(""); setIMsg(""); setIPass(""); setShowInterest(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setILoading(false);
    }
  };

  const features = [
    { Icon: MonitorSmartphone, title: de ? "Alle Geräte auf einen Blick" : "All devices at a glance", sub: de ? "Fahrzeuge, Maschinen, PCs & mehr" : "Vehicles, machines, PCs & more" },
    { Icon: Radio, title: de ? "Live-Status & Fernzugriff" : "Live status & remote access", sub: de ? "Online-Überwachung in Echtzeit" : "Real-time online monitoring" },
    { Icon: MapPin, title: de ? "Standorte, Tickets & Berichte" : "Locations, tickets & reports", sub: de ? "Eine Plattform für den Betrieb" : "One platform for operations" },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: "#F8FAFC" }} data-testid="login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative overflow-hidden flex-col justify-between p-12" style={{ background: "linear-gradient(155deg, #272C34 0%, #1A1D22 55%, #0E1013 100%)" }}>
        {/* soft red glow top-right */}
        <div className="absolute -top-32 -right-24 w-[460px] h-[460px] rounded-full blur-[140px] pointer-events-none" style={{ background: "rgba(192,0,0,0.30)" }} />
        {/* slim diagonal accent */}
        <div className="absolute inset-y-0 right-0 w-40 pointer-events-none" style={{ background: "linear-gradient(120deg, transparent 0%, rgba(192,0,0,0.16) 60%, rgba(192,0,0,0.28) 100%)", clipPath: "polygon(60% 0, 100% 0, 100% 100%, 20% 100%)" }} />
        {/* faint logo watermark */}
        <svg viewBox="0 0 32 32" className="absolute -bottom-10 -right-6 w-[280px] h-[280px] opacity-[0.05] pointer-events-none" aria-hidden>
          <path d="M5 27 L16 5 L20 5 L11 27 Z" fill="#ffffff" />
          <path d="M14 27 L24 8 L28 8 L20 27 Z" fill="#ffffff" />
        </svg>

        <div className="relative flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-black/30" style={{ background: "linear-gradient(135deg, #c00000 0%, #c00000 100%)" }}>
            <svg viewBox="0 0 32 32" className="w-9 h-9" aria-hidden>
              <path d="M4 27 L16 5 L21 5 L9 27 Z" fill="#ffffff" />
              <path d="M14 27 L25 6 L30 6 L19 27 Z" fill="#ffffff" opacity="0.8" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-3xl text-white tracking-tight">Fleet<span style={{ color: "#ffffff" }}>Area</span></div>
            <div className="text-[11px] text-gray-400 font-mono uppercase tracking-[0.2em] mt-0.5">{t("subtitle")}</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <h2 className="font-display font-bold text-white leading-[1.1]" style={{ fontSize: 40, letterSpacing: "-0.02em" }}>
            Fahrzeuge. Maschinen. IT.
          </h2>
          <p className="text-gray-300/90 mt-4 text-[15px] leading-relaxed">
            {de ? "Überwachen und verwalten Sie Ihre gesamte Geräteflotte über alle Standorte hinweg – in einer Oberfläche." : "Monitor and manage your entire device fleet across all locations — in one interface."}
          </p>
          <div className="mt-10 space-y-5">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <f.Icon className="w-5 h-5" style={{ color: "#c00000" }} />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{f.title}</div>
                  <div className="text-gray-400 text-[13px]">{f.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-[12px] text-gray-500 font-mono">© {new Date().getFullYear()} FleetArea</div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 lg:p-14">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3 lg:hidden">
              <svg viewBox="0 0 32 32" className="w-9 h-9" aria-hidden>
                <path d="M5 27 L16 5 L20 5 L11 27 Z" fill={RED} />
                <path d="M14 27 L24 8 L28 8 L20 27 Z" fill={RED} opacity="0.65" />
              </svg>
              <div className="font-display font-bold text-lg text-gray-900">Fleet<span style={{ color: RED }}>Area</span></div>
            </div>
            <div className="hidden lg:flex items-center gap-2 text-sm text-gray-500">
              <ShieldCheck className="w-4 h-4" style={{ color: RED }} /> {de ? "Sicherer Zugang" : "Secure access"}
            </div>
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(de ? "en" : "de")}
              className="text-xs font-mono px-2.5 py-1.5 rounded-md border border-gray-300 text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors"
            >
              {de ? "EN" : "DE"}
            </button>
          </div>

          <h1 className="font-display font-bold text-gray-900" style={{ fontSize: 30, letterSpacing: "-0.015em" }}>{t("login_title")}</h1>
          <p className="text-[15px] text-gray-500 mt-2 mb-8">{t("login_desc")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-2 block">
                {t("email")} / {t("username") || "Benutzername"}
              </label>
              <input
                data-testid="login-email"
                type="text"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all"
                placeholder={t("email_or_username") || "E-Mail oder Benutzername"}
                required
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-gray-600 mb-2 block">
                {t("password")}
              </label>
              <input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full disabled:opacity-60 text-white font-semibold rounded-xl px-4 py-3.5 text-[15px] transition-all hover:brightness-110 flex items-center justify-center gap-2 shadow-lg shadow-[#c00000]/20"
              style={{ background: RED }}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("sign_in")} <ArrowRight className="w-4 h-4" /></>}
              {loading && (de ? "Anmelden…" : "Signing in…")}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            {!showInterest ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-600">{de ? "Noch keinen Zugang?" : "No access yet?"}</span>
                <button data-testid="show-interest" onClick={() => setShowInterest(true)} className="text-sm font-semibold text-[#c00000] hover:underline">
                  {de ? "Zugang registrieren →" : "Register account →"}
                </button>
              </div>
            ) : (
              <form onSubmit={submitInterest} className="space-y-3" data-testid="interest-form">
                <div className="flex items-center justify-between">
                  <h2 className="font-display font-semibold text-gray-900 text-lg">{de ? "Zugang registrieren" : "Register account"}</h2>
                  <button type="button" data-testid="interest-cancel" onClick={() => setShowInterest(false)} className="text-xs text-gray-500 hover:text-gray-900">{de ? "Abbrechen" : "Cancel"}</button>
                </div>
                <p className="text-[13px] text-gray-500 -mt-1">{de ? "Konto anlegen – nach Prüfung schalten wir Ihren Portal-Zugang frei." : "Create an account – we activate your portal access after review."}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input data-testid="interest-name" value={iName} onChange={(e) => setIName(e.target.value)} required placeholder={de ? "Name *" : "Name *"} className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all" />
                  <input data-testid="interest-company" value={iCompany} onChange={(e) => setICompany(e.target.value)} placeholder={de ? "Firma" : "Company"} className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all" />
                </div>
                <input data-testid="interest-email" type="email" value={iEmail} onChange={(e) => setIEmail(e.target.value)} required placeholder={de ? "E-Mail *" : "Email *"} className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all" />
                <input data-testid="interest-password" type="password" value={iPass} onChange={(e) => setIPass(e.target.value)} required placeholder={de ? "Passwort (min. 6 Zeichen) *" : "Password (min. 6 chars) *"} className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all" />
                <textarea data-testid="interest-message" value={iMsg} onChange={(e) => setIMsg(e.target.value)} rows={2} placeholder={de ? "Nachricht (optional)" : "Message (optional)"} className="w-full bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#c00000] focus:ring-2 focus:ring-[#c00000]/20 outline-none transition-all resize-none" />
                <button data-testid="interest-submit" type="submit" disabled={iLoading} className="w-full disabled:opacity-60 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-all hover:brightness-110 flex items-center justify-center gap-2" style={{ background: RED }}>
                  {iLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {de ? "Registrieren" : "Register"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
